import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  Alert,
  Badge,
  Button,
  Chip,
  Code,
  Container,
  FileButton,
  Group,
  List,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconPlus,
  IconRepeat,
  IconTrash,
  IconUpload,
  IconX,
} from '@tabler/icons-react';

import { CatalogPanel, LogoPaper, PageTitle, type ArmedItem } from '@/components';
import { useDocxTemplateMapping } from '@/hooks';
import { vnextApiClient } from '@/services/vnext-api';
import type {
  AppearanceSet,
  Binding,
  BindingCatalog,
  BindingMap,
  BindingValidationReport,
  CatalogCollection,
  CatalogValue,
  RegionBlock,
  RegionTree,
  RepeatBinding,
  ReportConfig,
  StyleByRule,
  TemplateAnchorRequest,
} from '@/types';

const METADATA_KEY_RE = /^[a-zA-Z_][a-zA-Z0-9_-]*$/;

const SUBJECT_TYPE_OPTIONS = [
  { value: 'company', label: 'Company only' },
  { value: 'individual', label: 'Individual only' },
  { value: 'vessel', label: 'Vessel only' },
];

// The closed block vocabulary a narrative section's prose renders into; each
// maps to one of THIS template's style names (the per-config style contract).
const STYLE_SLOTS = [
  { key: 'paragraph', label: 'Paragraph / body text' },
  { key: 'bullet_item', label: 'Bullet list item' },
  { key: 'number_item', label: 'Numbered list item' },
] as const;

// ---------------------------------------------------------------- draft model

/** A loop (repeat) located in the document, with its nesting chain. */
interface LocatedLoop {
  binding: RepeatBinding;
  /** Path of repeat ids from root to (and including) this loop. */
  idChain: string[];
  /** Block span for block loops. */
  span?: { start: number; end: number };
  /** Table pos + row for row loops. */
  rowAt?: { pos: number; row: number };
}

function locateLoops(
  bindings: Binding[],
  regions: RegionTree,
  chain: string[] = []
): LocatedLoop[] {
  const out: LocatedLoop[] = [];
  for (const binding of bindings) {
    if (binding.kind !== 'repeat') {
      continue;
    }
    const idChain = [...chain, binding.id];
    const located: LocatedLoop = { binding, idChain };
    if ('bookmark_range' in binding.region) {
      const base = regions.bookmarks[binding.region.bookmark_range];
      const end = regions.bookmarks[`${binding.region.bookmark_range}_end`];
      if (base && end) {
        located.span = { start: base.start_pos, end: end.start_pos };
      }
    } else {
      const mark = regions.bookmarks[binding.region.bookmark];
      if (mark && mark.row !== undefined) {
        located.rowAt = { pos: mark.start_pos, row: mark.row };
      }
    }
    out.push(located);
    out.push(...locateLoops(binding.bindings ?? [], regions, idChain));
  }
  return out;
}

/** Innermost loops whose region contains the given block / cell. */
function enclosingLoops(
  loops: LocatedLoop[],
  target: { pos: number; row?: number }
): LocatedLoop[] {
  return loops
    .filter((loop) => {
      if (loop.span) {
        return target.pos >= loop.span.start && target.pos <= loop.span.end;
      }
      if (loop.rowAt) {
        return loop.rowAt.pos === target.pos && loop.rowAt.row === target.row;
      }
      return false;
    })
    .sort((a, b) => a.idChain.length - b.idChain.length);
}

function findRepeat(bindings: Binding[], idChain: string[]): RepeatBinding | null {
  let current = bindings;
  let found: RepeatBinding | null = null;
  for (const id of idChain) {
    const next = current.find((b): b is RepeatBinding => b.kind === 'repeat' && b.id === id);
    if (!next) {
      return null;
    }
    found = next;
    current = next.bindings ?? [];
  }
  return found;
}

// --- geometry-based re-nesting ----------------------------------------------
// A binding's structurally-correct parent is the innermost loop whose region
// physically contains its anchor — the same invariant the fill relies on
// (a loop clones its region, so anything inside it must be a child). Placement
// freezes nesting at bind time, so adding a loop around existing bindings
// leaves them mis-parented; this recomputes the tree from document geometry.

type LoopSpan =
  | { kind: 'range'; lo: number; hi: number }
  | { kind: 'row'; pos: number; row: number };
interface AnchorPoint {
  pos: number;
  row?: number;
}

function bindingAnchorName(b: Binding): string | null {
  if (b.kind === 'value') {
    return b.anchor.bookmark;
  }
  if (b.kind === 'section') {
    return 'bookmark' in b.anchor ? b.anchor.bookmark : b.anchor.bookmark_range;
  }
  return 'bookmark_range' in b.region ? b.region.bookmark_range : b.region.bookmark;
}

function anchorPointOf(b: Binding, regions: RegionTree): AnchorPoint | null {
  const name = bindingAnchorName(b);
  const mark = name ? regions.bookmarks[name] : undefined;
  return mark ? { pos: mark.start_pos, row: mark.row } : null;
}

function loopSpanOf(b: RepeatBinding, regions: RegionTree): LoopSpan | null {
  if ('bookmark_range' in b.region) {
    const base = regions.bookmarks[b.region.bookmark_range];
    const end = regions.bookmarks[`${b.region.bookmark_range}_end`];
    return base && end ? { kind: 'range', lo: base.start_pos, hi: end.start_pos } : null;
  }
  const mark = regions.bookmarks[b.region.bookmark];
  return mark && mark.row !== undefined
    ? { kind: 'row', pos: mark.start_pos, row: mark.row }
    : null;
}

function spanContains(span: LoopSpan, p: AnchorPoint): boolean {
  return span.kind === 'row'
    ? p.pos === span.pos && p.row === span.row
    : p.pos >= span.lo && p.pos <= span.hi;
}

/** Row loops (a single row) are tightest; among ranges, the smaller span wins. */
function spanSize(span: LoopSpan): number {
  return span.kind === 'row' ? 0 : span.hi - span.lo;
}

/** Rebuild the binding tree so each binding parents to the innermost loop
 * whose region contains its anchor. Preserves relative order within a parent.
 * Re-nesting only ever *moves* a binding it can positively locate inside a
 * tighter loop — a binding whose anchor can't be resolved in `regions` (e.g.
 * a bookmark just injected but not yet reflected in this snapshot) keeps its
 * CURRENT parent rather than being evicted to the root. Returns the new tree
 * plus the ids whose parent changed. */
export function renestByGeometry(
  bindings: Binding[],
  regions: RegionTree
): { bindings: Binding[]; movedIds: string[] } {
  const flat: Binding[] = [];
  const originalParent = new Map<string, string | null>();
  const collect = (list: Binding[], parent: string | null) => {
    for (const b of list) {
      originalParent.set(b.id, parent);
      flat.push(b.kind === 'repeat' ? { ...b, bindings: [] } : b);
      if (b.kind === 'repeat') {
        collect(b.bindings ?? [], b.id);
      }
    }
  };
  collect(bindings, null);

  const loops = flat
    .filter((b): b is RepeatBinding => b.kind === 'repeat')
    .map((b) => ({ b, span: loopSpanOf(b, regions) }))
    .filter((x): x is { b: RepeatBinding; span: LoopSpan } => x.span !== null);

  const byId = new Map(flat.map((b) => [b.id, b]));
  const root: Binding[] = [];
  const movedIds: string[] = [];

  for (const b of flat) {
    const pt = anchorPointOf(b, regions);
    let best: { b: RepeatBinding; span: LoopSpan } | null = null;
    if (pt) {
      for (const loop of loops) {
        if (loop.b.id === b.id || !spanContains(loop.span, pt)) {
          continue;
        }
        if (best === null || spanSize(loop.span) < spanSize(best.span)) {
          best = loop;
        }
      }
    }
    // best → the tightest containing loop. If we couldn't even locate the
    // anchor (pt === null), don't evict to root: keep the current parent so a
    // stale-regions snapshot can never strand a deliberately-placed binding.
    const newParent = best ? best.b.id : pt === null ? (originalParent.get(b.id) ?? null) : null;
    if (newParent !== (originalParent.get(b.id) ?? null)) {
      movedIds.push(b.id);
    }
    if (newParent === null) {
      root.push(b);
    } else {
      const parentNode = byId.get(newParent) as RepeatBinding;
      parentNode.bindings = [...(parentNode.bindings ?? []), b];
    }
  }
  return { bindings: root, movedIds };
}

/** Insert a binding under the innermost existing loop containing its target
 * (pure; used by both the live insert and the synchronous re-nest path). */
function insertBindingInto(
  bindings: Binding[],
  binding: Binding,
  target: { pos: number; row?: number },
  regions: RegionTree
): Binding[] {
  const clone: Binding[] = JSON.parse(JSON.stringify(bindings));
  const innermost = enclosingLoops(locateLoops(clone, regions), target).at(-1);
  if (innermost) {
    const repeat = findRepeat(clone, innermost.idChain);
    if (repeat) {
      repeat.bindings = [...(repeat.bindings ?? []), binding];
      return clone;
    }
  }
  clone.push(binding);
  return clone;
}

function countBindings(bindings: Binding[]): number {
  return bindings.reduce(
    (n, b) => n + 1 + (b.kind === 'repeat' ? countBindings(b.bindings ?? []) : 0),
    0
  );
}

function nextBindingId(map: BindingMap): string {
  let max = 0;
  const walk = (bindings: Binding[]) => {
    for (const b of bindings) {
      const match = /^b(\d+)$/.exec(b.id);
      if (match) {
        max = Math.max(max, Number(match[1]));
      }
      if (b.kind === 'repeat') {
        walk(b.bindings ?? []);
      }
    }
  };
  walk(map.bindings);
  return `b${max + 1}`;
}

/** All non-repeat bindings keyed by their anchor bookmark name. */
function anchoredBindings(bindings: Binding[]): Map<string, Binding> {
  const out = new Map<string, Binding>();
  const walk = (list: Binding[]) => {
    for (const b of list) {
      if (b.kind === 'value') {
        out.set(b.anchor.bookmark, b);
      } else if (b.kind === 'section') {
        out.set('bookmark' in b.anchor ? b.anchor.bookmark : b.anchor.bookmark_range, b);
      } else {
        walk(b.bindings ?? []);
      }
    }
  };
  walk(bindings);
  return out;
}

/** The record fields of the collection a loop iterates, found by its `over`
 * path (top-level or nested) — the candidate `style_by` sources. */
function collectionFields(over: string, catalog: BindingCatalog): CatalogValue[] {
  const search = (cols: CatalogCollection[]): CatalogValue[] | null => {
    for (const c of cols) {
      if (c.path === over) {
        return c.values;
      }
      const nested = search(c.collections ?? []);
      if (nested) {
        return nested;
      }
    }
    return null;
  };
  return search(catalog.subjects.collections ?? []) ?? [];
}

/** Every closed-vocabulary (enum) field in the catalog — the fields an
 * appearance set can be built for. Walks report values + the whole subjects
 * collection tree; an enum with no `allowed_values` can't seed a vocabulary,
 * so it's skipped. */
function enumFields(catalog: BindingCatalog): CatalogValue[] {
  const out: CatalogValue[] = [];
  const collect = (values: CatalogValue[]) => {
    for (const v of values) {
      if (v.type === 'enum' && v.allowed_values && v.allowed_values.length > 0) {
        out.push(v);
      }
    }
  };
  collect(catalog.report_values);
  const walk = (col: CatalogCollection) => {
    collect(col.values);
    for (const c of col.collections ?? []) {
      walk(c);
    }
  };
  walk(catalog.subjects);
  return out;
}

/** The visible text of the cell/paragraph an exemplar bookmark marks, so a
 * matched appearance row can show what the author is pointing at. */
function bookmarkPreview(bookmark: string, regions: RegionTree): string | null {
  const span = regions.bookmarks[bookmark];
  if (!span) {
    return null;
  }
  const block = regions.blocks.find((b) => b.pos === span.start_pos);
  if (!block) {
    return null;
  }
  if (block.table && span.row !== undefined && span.col !== undefined) {
    const cell = block.table.rows
      .flatMap((r) => r.cells)
      .find((c) => c.row === span.row && c.col === span.col);
    return cell ? (cell.text ?? cell.preview) : null;
  }
  return block.text ?? block.preview;
}

/** Set (or clear, with null) a repeat binding's `style_by`, in place by id. */
function setBindingStyleBy(bindings: Binding[], id: string, rule: StyleByRule | null): Binding[] {
  return bindings.map((b) => {
    if (b.kind !== 'repeat') {
      return b;
    }
    if (b.id === id) {
      return { ...b, style_by: rule ?? undefined };
    }
    return { ...b, bindings: setBindingStyleBy(b.bindings ?? [], id, rule) };
  });
}

function pruneBinding(bindings: Binding[], id: string): Binding[] {
  return bindings
    .filter((b) => b.id !== id)
    .map((b) => (b.kind === 'repeat' ? { ...b, bindings: pruneBinding(b.bindings ?? [], id) } : b));
}

// -------------------------------------------------------------- pending model

type Pending =
  | {
      kind: 'value';
      target: { pos: number; row?: number; col?: number };
      path: string;
      label: string;
      /** Carried from the armed catalog value so the card can offer
       * "Colour by value" on enum-typed status cells. */
      valueType?: string;
      allowedValues?: string[] | null;
    }
  | { kind: 'rowLoop'; pos: number; row: number; over: string; label: string }
  | { kind: 'blockLoop'; start: number; end: number; over: string; label: string }
  | { kind: 'section'; start: number; end: number; slug: string; name: string; isData: boolean }
  | { kind: 'subjectLoop'; start: number; end: number }
  | { kind: 'gate'; start: number; end: number }
  | { kind: 'emptyState'; start: number; end: number }
  | { kind: 'inspect'; binding: Binding }
  | { kind: 'newKey' };

const RANGE_ARMED: Array<ArmedItem['type']> = [
  'collection',
  'component',
  'subjectLoop',
  'gate',
  'emptyState',
];

export function ReportConfigDocxMappingPage() {
  const { configId } = useParams<{ configId: string }>();
  const mapping = useDocxTemplateMapping(configId ?? null);
  const [config, setConfig] = useState<ReportConfig | null>(null);
  const [draft, setDraft] = useState<BindingMap>({ version: 1, bindings: [] });
  const [dirty, setDirty] = useState(false);
  const [armed, setArmed] = useState<ArmedItem | null>(null);
  const [pinStart, setPinStart] = useState<number | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [styleModalOpen, setStyleModalOpen] = useState(false);
  const [styleDraft, setStyleDraft] = useState<Record<string, string>>({});
  const [appearanceModalOpen, setAppearanceModalOpen] = useState(false);
  const [appearanceDraft, setAppearanceDraft] = useState<Record<string, AppearanceSet>>({});
  // Canvas pick modes for the appearance builder — while one is set, canvas
  // clicks feed the builder (match a legend / re-pick one exemplar) instead of
  // creating a binding. The modal is closed so the template is clickable.
  const [legendPickFor, setLegendPickFor] = useState<{ set: string } | null>(null);
  const [exemplarPickFor, setExemplarPickFor] = useState<{ set: string; value: string } | null>(
    null
  );
  // Pending-action options
  const [replaceText, setReplaceText] = useState('');
  const [valueFormat, setValueFormat] = useState<string | null>(null);
  const [appearanceSetName, setAppearanceSetName] = useState<string | null>(null);
  const [keepContent, setKeepContent] = useState(false);
  const [gateType, setGateType] = useState<string | null>(null);
  const [emptyTargetChain, setEmptyTargetChain] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [isWorking, setIsWorking] = useState(false);

  useEffect(() => {
    if (!configId) {
      return;
    }
    vnextApiClient
      .getReportConfig(configId)
      .then((cfg) => {
        setConfig(cfg);
        if (cfg.bindingMap) {
          setDraft(cfg.bindingMap);
        }
        if (cfg.styleMap) {
          setStyleDraft(cfg.styleMap);
        }
        if (cfg.appearanceSets) {
          setAppearanceDraft(cfg.appearanceSets);
        }
      })
      .catch(() => notifications.show({ color: 'red', message: 'Failed to load the config' }));
  }, [configId]);

  const loops = useMemo(
    () => (mapping.regions ? locateLoops(draft.bindings, mapping.regions) : []),
    [draft, mapping.regions]
  );
  const anchors = useMemo(() => anchoredBindings(draft.bindings), [draft]);

  const resetPending = useCallback(() => {
    setPending(null);
    setPinStart(null);
    setReplaceText('');
    setValueFormat(null);
    setAppearanceSetName(null);
    setKeepContent(false);
    setGateType(null);
    setEmptyTargetChain(null);
  }, []);

  const arm = useCallback(
    (item: ArmedItem | null) => {
      setArmed(item);
      resetPending();
    },
    [resetPending]
  );

  // ------------------------------------------------- appearance builder picks

  /** Auto-match a set's vocabulary against the legend table at `pos`: for each
   * value, find the cell whose text contains it, inject a point bookmark, and
   * record value → bookmark. Injects all matches in one call so bookmarks align
   * with the match order. Unmatched values stay unset (re-pick covers them). */
  const matchLegend = useCallback(
    async (pos: number) => {
      if (!legendPickFor || !mapping.regions) {
        return;
      }
      const setName = legendPickFor.set;
      const set = appearanceDraft[setName];
      const block = mapping.regions.blocks.find((b) => b.pos === pos);
      if (!set || !block?.table) {
        notifications.show({ color: 'orange', message: 'Click a legend table to match values' });
        return;
      }
      const cells = block.table.rows.flatMap((r) => r.cells);
      const matches: Array<{ value: string; row: number; col: number }> = [];
      for (const value of Object.keys(set.map)) {
        const needle = value.trim().toLowerCase();
        const cell = cells.find((c) => (c.text ?? c.preview ?? '').toLowerCase().includes(needle));
        if (cell) {
          matches.push({ value, row: cell.row, col: cell.col });
        }
      }
      if (matches.length === 0) {
        notifications.show({ color: 'orange', message: 'No legend cell matched this vocabulary' });
        return;
      }
      const { bookmarks } = await mapping.injectAnchors(
        matches.map((m) => ({ kind: 'point', pos, row: m.row, col: m.col }))
      );
      setAppearanceDraft((prev) => {
        const current = prev[setName];
        if (!current) {
          return prev;
        }
        const map = { ...current.map };
        matches.forEach((m, i) => {
          map[m.value] = bookmarks[i];
        });
        return { ...prev, [setName]: { ...current, map } };
      });
      setLegendPickFor(null);
      setAppearanceModalOpen(true);
      notifications.show({
        color: 'green',
        message: `Matched ${matches.length} of ${Object.keys(set.map).length} value(s)`,
      });
    },
    [legendPickFor, appearanceDraft, mapping]
  );

  /** Re-pick one value's exemplar: inject a single bookmark at the clicked
   * cell/paragraph and record value → bookmark. */
  const pickExemplar = useCallback(
    async (pos: number, row?: number, col?: number) => {
      if (!exemplarPickFor) {
        return;
      }
      const { set: setName, value } = exemplarPickFor;
      const {
        bookmarks: [bookmark],
      } = await mapping.injectAnchors([{ kind: 'point', pos, row, col }]);
      setAppearanceDraft((prev) => {
        const current = prev[setName];
        if (!current) {
          return prev;
        }
        return { ...prev, [setName]: { ...current, map: { ...current.map, [value]: bookmark } } };
      });
      setExemplarPickFor(null);
      setAppearanceModalOpen(true);
      notifications.show({ color: 'green', message: `Set exemplar for "${value}"` });
    },
    [exemplarPickFor, mapping]
  );

  // ------------------------------------------------------- click dispatching

  const handleBlockClick = useCallback(
    (pos: number) => {
      if (legendPickFor) {
        void matchLegend(pos);
        return;
      }
      if (exemplarPickFor) {
        void pickExemplar(pos);
        return;
      }
      if (!armed) {
        return;
      }
      if (armed.type === 'value') {
        setPending({
          kind: 'value',
          target: { pos },
          path: armed.path,
          label: armed.label,
          valueType: armed.valueType,
          allowedValues: armed.allowedValues,
        });
        return;
      }
      if (RANGE_ARMED.includes(armed.type)) {
        if (pinStart === null) {
          setPinStart(pos);
          return;
        }
        const [start, end] = [Math.min(pinStart, pos), Math.max(pinStart, pos)];
        setPinStart(null);
        if (armed.type === 'collection') {
          setPending({ kind: 'blockLoop', start, end, over: armed.path, label: armed.label });
        } else if (armed.type === 'component') {
          setPending({
            kind: 'section',
            start,
            end,
            slug: armed.slug,
            name: armed.name,
            isData: armed.isData,
          });
        } else if (armed.type === 'subjectLoop') {
          setPending({ kind: 'subjectLoop', start, end });
        } else if (armed.type === 'gate') {
          setPending({ kind: 'gate', start, end });
        } else {
          setPending({ kind: 'emptyState', start, end });
        }
      }
    },
    [armed, pinStart, legendPickFor, exemplarPickFor, matchLegend, pickExemplar]
  );

  const handleCellClick = useCallback(
    (pos: number, row: number, col: number) => {
      if (legendPickFor) {
        void matchLegend(pos);
        return;
      }
      if (exemplarPickFor) {
        void pickExemplar(pos, row, col);
        return;
      }
      if (armed?.type === 'value') {
        setPending({
          kind: 'value',
          target: { pos, row, col },
          path: armed.path,
          label: armed.label,
          valueType: armed.valueType,
          allowedValues: armed.allowedValues,
        });
        return;
      }
      // Range gestures treat a click anywhere in a table as a pin on the
      // table block — cells otherwise swallow the click, leaving table
      // blocks unselectable as range endpoints.
      if (armed && RANGE_ARMED.includes(armed.type)) {
        handleBlockClick(pos);
      }
    },
    [armed, handleBlockClick, legendPickFor, exemplarPickFor, matchLegend, pickExemplar]
  );

  const handleRowClick = useCallback(
    (pos: number, row: number) => {
      if (armed?.type === 'collection') {
        setPending({ kind: 'rowLoop', pos, row, over: armed.path, label: armed.label });
        return;
      }
      if (armed && RANGE_ARMED.includes(armed.type)) {
        handleBlockClick(pos);
      }
    },
    [armed, handleBlockClick]
  );

  const handleAnnotationClick = useCallback((binding: Binding) => {
    setPending({ kind: 'inspect', binding });
  }, []);

  // ------------------------------------------------------------ confirmation

  const insertAt = useCallback(
    (binding: Binding, target: { pos: number; row?: number }) => {
      setDraft((prev) => ({
        ...prev,
        bindings: insertBindingInto(prev.bindings, binding, target, mapping.regions!),
      }));
      setDirty(true);
    },
    [mapping.regions]
  );

  /** Insert a loop, then re-nest the whole tree by geometry so existing
   * bindings that now fall inside the loop's region become its children.
   * Returns the resulting map (computed synchronously from the current draft)
   * so the caller can auto-validate it. */
  const insertLoopAndRenest = useCallback(
    (binding: Binding, targetPos: number, regionsOverride?: RegionTree): BindingMap => {
      // Prefer the fresh regions handed back by injectAnchors: `mapping.regions`
      // state hasn't updated yet this tick, so it lacks the just-injected loop
      // bookmark — re-nesting against it would fail to locate the new loop.
      const regions = regionsOverride ?? mapping.regions!;
      const inserted = insertBindingInto(draft.bindings, binding, { pos: targetPos }, regions);
      const { bindings, movedIds } = renestByGeometry(inserted, regions);
      const next: BindingMap = { ...draft, bindings };
      setDraft(next);
      setDirty(true);
      if (movedIds.length > 0) {
        notifications.show({
          color: 'blue',
          message: `Re-nested ${movedIds.length} existing binding${
            movedIds.length === 1 ? '' : 's'
          } into the loop by position`,
        });
      }
      return next;
    },
    [draft, mapping.regions]
  );

  const confirmPending = useCallback(async () => {
    if (!pending || !mapping.regions) {
      return;
    }
    setIsWorking(true);
    try {
      const id = nextBindingId(draft);
      if (pending.kind === 'value') {
        const request: TemplateAnchorRequest = {
          kind: 'point',
          pos: pending.target.pos,
          row: pending.target.row,
          col: pending.target.col,
        };
        const {
          bookmarks: [bookmark],
        } = await mapping.injectAnchors([request]);
        insertAt(
          {
            id,
            kind: 'value',
            anchor: { bookmark },
            source: relativeSource(pending.path, pending.target),
            // An image is placed whole as a picture, by the image format only.
            ...(pending.valueType === 'image'
              ? { format: 'image' }
              : {
                  ...(replaceText.trim() ? { replace_text: replaceText } : {}),
                  ...(valueFormat ? { format: valueFormat } : {}),
                }),
            ...(appearanceSetName
              ? { appearance_by: { set: appearanceSetName, keep_content: keepContent } }
              : {}),
          },
          pending.target
        );
      } else if (pending.kind === 'rowLoop') {
        const {
          bookmarks: [bookmark],
          regions: fresh,
        } = await mapping.injectAnchors([{ kind: 'point', pos: pending.pos, row: pending.row }]);
        const next = insertLoopAndRenest(
          { id, kind: 'repeat', over: pending.over, region: { bookmark }, bindings: [] },
          pending.pos,
          fresh
        );
        await mapping.validate(next).catch(() => undefined);
      } else if (
        pending.kind === 'blockLoop' ||
        pending.kind === 'subjectLoop' ||
        pending.kind === 'gate' ||
        pending.kind === 'section'
      ) {
        const {
          bookmarks: [bookmark],
          regions: fresh,
        } = await mapping.injectAnchors([
          { kind: 'range', pos: pending.start, end_pos: pending.end },
        ]);
        let binding: Binding;
        if (pending.kind === 'blockLoop') {
          binding = {
            id,
            kind: 'repeat',
            over: pending.over,
            region: { bookmark_range: bookmark },
            bindings: [],
          };
        } else if (pending.kind === 'subjectLoop') {
          binding = {
            id,
            kind: 'repeat',
            over: 'subjects',
            region: { bookmark_range: bookmark },
            bindings: [],
          };
        } else if (pending.kind === 'gate') {
          if (!gateType) {
            notifications.show({ color: 'orange', message: 'Pick a subject type first' });
            return;
          }
          binding = {
            id,
            kind: 'section',
            anchor: { bookmark_range: bookmark },
            applicable_subject_types: [gateType],
          };
        } else {
          binding = {
            id,
            kind: 'section',
            anchor: { bookmark_range: bookmark },
            component: pending.slug,
            ...(pending.isData ? {} : { style_map: config?.styleContract ?? undefined }),
          };
        }
        if (pending.kind === 'blockLoop' || pending.kind === 'subjectLoop') {
          // A new loop can enclose already-placed bindings — re-nest by
          // geometry so they become its children, then surface anything that
          // no longer resolves in the new scope.
          const next = insertLoopAndRenest(binding, pending.start, fresh);
          await mapping.validate(next).catch(() => undefined);
        } else {
          insertAt(binding, { pos: pending.start });
        }
      } else if (pending.kind === 'emptyState') {
        if (!emptyTargetChain) {
          notifications.show({
            color: 'orange',
            message: 'Pick the loop this empty state belongs to',
          });
          return;
        }
        const {
          bookmarks: [bookmark],
        } = await mapping.injectAnchors([
          { kind: 'range', pos: pending.start, end_pos: pending.end },
        ]);
        setDraft((prev) => {
          const clone: BindingMap = JSON.parse(JSON.stringify(prev));
          const repeat = findRepeat(clone.bindings, emptyTargetChain.split('>'));
          if (repeat) {
            repeat.empty_region = { bookmark_range: bookmark };
          }
          return clone;
        });
        setDirty(true);
      }
      resetPending();
      setArmed(null);
    } catch (err) {
      notifications.show({
        color: 'red',
        message: `Failed: ${err instanceof Error ? err.message : err}`,
      });
    } finally {
      setIsWorking(false);
    }
  }, [
    pending,
    mapping,
    draft,
    insertAt,
    insertLoopAndRenest,
    replaceText,
    valueFormat,
    appearanceSetName,
    keepContent,
    gateType,
    emptyTargetChain,
    config,
    resetPending,
  ]);

  /** Record-relative source when the target sits inside a loop over the
   * value's collection; the catalog's record fields are already relative,
   * so only prefix-stripping for absolute paths applies at root. */
  const relativeSource = useCallback(
    (path: string, _target: { pos: number; row?: number }) => path,
    []
  );

  const createMetadataKey = useCallback(async () => {
    const key = newKeyName.trim();
    if (!configId || !key || !METADATA_KEY_RE.test(key)) {
      return;
    }
    setIsWorking(true);
    try {
      const updated = await vnextApiClient.updateReportConfig(configId, {
        report_metadata_defaults: {
          ...(config?.reportMetadataDefaults ?? {}),
          [key]: newKeyValue,
        },
      });
      setConfig(updated);
      setNewKeyName('');
      setNewKeyValue('');
      mapping.refresh();
      resetPending();
      notifications.show({ color: 'green', message: `Created report_metadata.${key}` });
    } catch {
      notifications.show({ color: 'red', message: 'Failed to create the key' });
    } finally {
      setIsWorking(false);
    }
  }, [configId, config, newKeyName, newKeyValue, mapping, resetPending]);

  const deleteBinding = useCallback((id: string) => {
    setDraft((prev) => ({ ...prev, bindings: pruneBinding(prev.bindings, id) }));
    setDirty(true);
    setPending(null);
  }, []);

  const setStyleBy = useCallback((id: string, rule: StyleByRule | null) => {
    setDraft((prev) => ({ ...prev, bindings: setBindingStyleBy(prev.bindings, id, rule) }));
    setDirty(true);
    setPending((p) =>
      p && p.kind === 'inspect' && p.binding.id === id
        ? {
            kind: 'inspect',
            binding: { ...(p.binding as RepeatBinding), style_by: rule ?? undefined },
          }
        : p
    );
  }, []);

  /** Remove a binding from the validation panel and immediately re-validate,
   * so the resolved-away issue drops off the list without a manual re-check. */
  const removeUnresolved = useCallback(
    (id: string) => {
      const next: BindingMap = { ...draft, bindings: pruneBinding(draft.bindings, id) };
      setDraft(next);
      setDirty(true);
      void mapping.validate(next).catch(() => undefined);
    },
    [draft, mapping]
  );

  const saveStyleMap = useCallback(async () => {
    if (!configId) {
      return;
    }
    try {
      const updated = await vnextApiClient.updateReportConfig(configId, {
        style_map: styleDraft,
      });
      setConfig(updated);
      setStyleModalOpen(false);
      notifications.show({ color: 'green', message: 'Style contract saved' });
      if (mapping.catalog) {
        void mapping
          .validate({ ...draft, schema_versions: mapping.catalog.schema_versions })
          .catch(() => undefined);
      }
    } catch {
      notifications.show({ color: 'red', message: 'Failed to save the style contract' });
    }
  }, [configId, styleDraft, draft, mapping]);

  const saveAppearanceSets = useCallback(async () => {
    if (!configId) {
      return;
    }
    // Drop unmatched value rows (empty bookmark) — they're WIP placeholders, not
    // fillable mappings; the engine expects every map entry to name an exemplar.
    const cleaned: Record<string, AppearanceSet> = {};
    for (const [name, set] of Object.entries(appearanceDraft)) {
      const map = Object.fromEntries(Object.entries(set.map).filter(([, bm]) => bm));
      cleaned[name] = { ...set, map };
    }
    try {
      const updated = await vnextApiClient.updateReportConfig(configId, {
        appearance_sets: cleaned,
      });
      setConfig(updated);
      setAppearanceDraft(cleaned);
      setAppearanceModalOpen(false);
      notifications.show({ color: 'green', message: 'Appearance sets saved' });
      if (mapping.catalog) {
        void mapping
          .validate({ ...draft, schema_versions: mapping.catalog.schema_versions })
          .catch(() => undefined);
      }
    } catch {
      notifications.show({ color: 'red', message: 'Failed to save appearance sets' });
    }
  }, [configId, appearanceDraft, draft, mapping]);

  /** Enter legend-match mode: disarm, close the modal so the canvas is
   * clickable, then wait for a click on the legend table. */
  const startLegendPick = useCallback(
    (setName: string) => {
      arm(null);
      setExemplarPickFor(null);
      setAppearanceModalOpen(false);
      setLegendPickFor({ set: setName });
    },
    [arm]
  );

  /** Enter single-exemplar re-pick mode for one value. */
  const startExemplarPick = useCallback(
    (setName: string, value: string) => {
      arm(null);
      setLegendPickFor(null);
      setAppearanceModalOpen(false);
      setExemplarPickFor({ set: setName, value });
    },
    [arm]
  );

  const cancelPickMode = useCallback(() => {
    setLegendPickFor(null);
    setExemplarPickFor(null);
    setAppearanceModalOpen(true);
  }, []);

  const enumFieldOptions = useMemo(
    () => (mapping.catalog ? enumFields(mapping.catalog) : []),
    [mapping.catalog]
  );

  const handleSave = useCallback(async () => {
    if (!mapping.catalog) {
      return;
    }
    const map: BindingMap = { ...draft, schema_versions: mapping.catalog.schema_versions };
    try {
      await mapping.saveBindingMap(map);
      setDraft(map);
      setDirty(false);
      notifications.show({ color: 'green', message: 'Binding map saved' });
    } catch {
      notifications.show({ color: 'red', message: 'Failed to save the binding map' });
    }
  }, [draft, mapping]);

  const handleValidate = useCallback(async () => {
    try {
      const report = await mapping.validate(draft);
      notifications.show(
        report.valid
          ? { color: 'green', message: 'Binding map is valid' }
          : { color: 'orange', message: `${report.issues.length} issue(s) found` }
      );
    } catch (err) {
      notifications.show({
        color: 'red',
        message: `Validation failed: ${err instanceof Error ? err.message : err}`,
      });
    }
  }, [draft, mapping]);

  const doUpload = useCallback(
    async (file: File, clearBindings: boolean) => {
      try {
        // A new template has none of the previously-injected bookmarks, so
        // keeping the old bindings would strand them (dead anchors) and any
        // re-binding would duplicate them. Clear the map as part of replacing.
        if (clearBindings) {
          const empty: BindingMap = { version: 1, bindings: [] };
          await mapping.saveBindingMap(empty);
          setDraft(empty);
          setDirty(false);
        }
        await mapping.uploadTemplate(file);
        notifications.show({ color: 'green', message: 'Template uploaded' });
      } catch (err) {
        notifications.show({
          color: 'red',
          message: `Upload failed: ${err instanceof Error ? err.message : err}`,
        });
      }
    },
    [mapping]
  );

  const handleUpload = useCallback(
    (file: File | null) => {
      if (!file) {
        return;
      }
      // Replacing an existing template with existing bindings must confirm —
      // the swap invalidates every anchor. First upload has nothing to lose.
      if (mapping.regions && draft.bindings.length > 0) {
        setReplaceFile(file);
        return;
      }
      void doUpload(file, false);
    },
    [mapping.regions, draft.bindings.length, doUpload]
  );

  if (!configId) {
    return null;
  }

  const activeLoopPaths = loops.map((loop) => loop.binding.over);

  // Full text of a pending value binding's target, for live replace_text
  // verification (blocks/cells carry `text` whenever `preview` truncates).
  let pendingTargetText: string | undefined;
  if (pending?.kind === 'value' && mapping.regions) {
    const block = mapping.regions.blocks.find((b) => b.pos === pending.target.pos);
    if (block) {
      if (pending.target.row !== undefined) {
        const cell = block.table?.rows
          .flatMap((r) => r.cells)
          .find((c) => c.row === pending.target.row && c.col === pending.target.col);
        pendingTargetText = cell ? (cell.text ?? cell.preview) : undefined;
      } else {
        pendingTargetText = block.text ?? block.preview;
      }
    }
  }

  const totalBindingCount = countBindings(draft.bindings);

  return (
    <Container fluid py="md" px="lg">
      <Modal
        opened={replaceFile !== null}
        onClose={() => setReplaceFile(null)}
        title="Replace template?"
      >
        <Text size="sm">
          This configuration has {totalBindingCount} binding
          {totalBindingCount === 1 ? '' : 's'}. Replacing the template invalidates every one of them
          — the new document contains none of their injected bookmarks. To avoid stranded,
          duplicated bindings, the mapping will be cleared when you replace.
        </Text>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setReplaceFile(null)}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={() => {
              const file = replaceFile;
              setReplaceFile(null);
              if (file) {
                void doUpload(file, true);
              }
            }}
          >
            Replace and clear bindings
          </Button>
        </Group>
      </Modal>
      <Modal
        opened={styleModalOpen}
        onClose={() => setStyleModalOpen(false)}
        title="Style contract"
      >
        <Text size="sm" mb="sm">
          Narrative sections render prose into these block types. Map each to a style your template
          defines, so headings/lists inherit the template&apos;s styling.
        </Text>
        <Stack gap="sm">
          {STYLE_SLOTS.map((slot) => (
            <Select
              key={slot.key}
              label={slot.label}
              placeholder="Choose a template style"
              searchable
              data={mapping.regions?.styles ?? []}
              value={styleDraft[slot.key] ?? null}
              onChange={(v) =>
                setStyleDraft((prev) => {
                  const next = { ...prev };
                  if (v) {
                    next[slot.key] = v;
                  } else {
                    delete next[slot.key];
                  }
                  return next;
                })
              }
            />
          ))}
        </Stack>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setStyleModalOpen(false)}>
            Cancel
          </Button>
          <Button onClick={saveStyleMap} disabled={STYLE_SLOTS.some((s) => !styleDraft[s.key])}>
            Save style contract
          </Button>
        </Group>
      </Modal>
      <Modal
        opened={appearanceModalOpen}
        onClose={() => setAppearanceModalOpen(false)}
        title="Colour coding"
        size="lg"
      >
        <Text size="sm" mb="xs">
          Colour-code a report cell by its value — for example, show a risk rating in red, amber, or
          green. It works by copying styling you have already drawn in the template:
        </Text>
        <List type="ordered" size="sm" mb="sm" spacing={4}>
          <List.Item>
            In your Word template, draw a small legend — one example cell for each possible value,
            each styled the way that value should look.
          </List.Item>
          <List.Item>
            Below, pick the field to colour by, then match each of its values to the example cell
            you drew for it.
          </List.Item>
          <List.Item>
            When you bind a real cell, turn on &ldquo;Colour by value&rdquo; and it copies the
            matching example cell&apos;s styling.
          </List.Item>
        </List>
        <AppearanceSetBuilder
          sets={appearanceDraft}
          onChange={setAppearanceDraft}
          enumFieldOptions={enumFieldOptions}
          regions={mapping.regions}
          onMatchLegend={startLegendPick}
          onRepick={startExemplarPick}
        />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setAppearanceModalOpen(false)}>
            Close
          </Button>
          <Button onClick={saveAppearanceSets}>Save colour coding</Button>
        </Group>
      </Modal>
      <Group justify="space-between" mb="md">
        <Group>
          <Button
            component={Link}
            to={`/admin/report-configs/${configId}`}
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
          >
            Back to config
          </Button>
          <PageTitle title={`Template mapping${config ? ` — ${config.name}` : ''}`} />
        </Group>
        <Group>
          <FileButton onChange={handleUpload} accept=".docx">
            {(props) => (
              <Button {...props} variant="default" leftSection={<IconUpload size={16} />}>
                {mapping.regions ? 'Replace template' : 'Upload template'}
              </Button>
            )}
          </FileButton>
          {mapping.regions && (
            <Button variant="default" onClick={() => setStyleModalOpen(true)}>
              Style contract
            </Button>
          )}
          {mapping.regions && (
            <Button variant="default" onClick={() => setAppearanceModalOpen(true)}>
              Colour coding
            </Button>
          )}
          <Button variant="default" onClick={handleValidate} loading={mapping.isValidating}>
            Validate
          </Button>
          <Button onClick={handleSave} loading={mapping.isSaving} disabled={!dirty}>
            Save binding map
          </Button>
        </Group>
      </Group>

      {mapping.validation && !mapping.validation.valid && (
        <ValidationIssues report={mapping.validation} onRemove={removeUnresolved} />
      )}

      {mapping.isLoading && <Loader />}

      {!mapping.isLoading && !mapping.regions && (
        <LogoPaper>
          <Stack align="center" py="xl">
            <Text>
              This configuration has no docx template yet. Upload the fully-styled .docx built in
              Word to start mapping.
            </Text>
          </Stack>
        </LogoPaper>
      )}

      {mapping.regions && mapping.catalog && (
        <Group align="stretch" gap="md" wrap="nowrap" style={{ height: 'calc(100vh - 160px)' }}>
          <Paper withBorder style={{ flex: '1 1 72%', minWidth: 0, position: 'relative' }}>
            {armed && (
              <Alert
                color="blue"
                py={4}
                px="sm"
                radius={0}
                title={undefined}
                withCloseButton
                onClose={() => arm(null)}
              >
                <Text size="xs">{armedHint(armed, pinStart)}</Text>
              </Alert>
            )}
            {(legendPickFor || exemplarPickFor) && (
              <Alert
                color="grape"
                py={4}
                px="sm"
                radius={0}
                withCloseButton
                onClose={cancelPickMode}
              >
                <Text size="xs">
                  {legendPickFor
                    ? `Colour coding “${legendPickFor.set}” — click your legend (the row of example cells). Each value is matched to the example whose text contains it.`
                    : `Choosing the example cell for “${exemplarPickFor?.value}” — click it in your legend.`}
                </Text>
              </Alert>
            )}
            <ScrollArea
              h={armed || legendPickFor || exemplarPickFor ? 'calc(100% - 40px)' : '100%'}
              p="md"
            >
              <Stack gap={2}>
                {mapping.regions.blocks.map((block) => (
                  <CanvasBlock
                    key={block.pos}
                    block={block}
                    regions={mapping.regions!}
                    loops={loops}
                    anchors={anchors}
                    pinStart={pinStart}
                    onBlockClick={handleBlockClick}
                    onCellClick={handleCellClick}
                    onRowClick={handleRowClick}
                    onAnnotationClick={handleAnnotationClick}
                  />
                ))}
              </Stack>
            </ScrollArea>

            {pending && (
              <PendingCard
                pending={pending}
                targetText={pendingTargetText}
                loops={loops}
                isWorking={isWorking}
                replaceText={replaceText}
                setReplaceText={setReplaceText}
                valueFormat={valueFormat}
                setValueFormat={setValueFormat}
                appearanceSetNames={Object.keys(config?.appearanceSets ?? {})}
                appearanceSetName={appearanceSetName}
                setAppearanceSetName={setAppearanceSetName}
                keepContent={keepContent}
                setKeepContent={setKeepContent}
                gateType={gateType}
                setGateType={setGateType}
                emptyTargetChain={emptyTargetChain}
                setEmptyTargetChain={setEmptyTargetChain}
                newKeyName={newKeyName}
                setNewKeyName={setNewKeyName}
                newKeyValue={newKeyValue}
                setNewKeyValue={setNewKeyValue}
                onCreateKey={createMetadataKey}
                onConfirm={confirmPending}
                onCancel={resetPending}
                onDelete={deleteBinding}
                styleByFields={
                  pending.kind === 'inspect' && pending.binding.kind === 'repeat' && mapping.catalog
                    ? collectionFields(pending.binding.over, mapping.catalog)
                    : []
                }
                styleOptions={mapping.regions?.styles ?? []}
                onSetStyleBy={setStyleBy}
              />
            )}
          </Paper>

          <Paper withBorder style={{ flex: '0 0 300px', minWidth: 260 }}>
            <CatalogPanel
              catalog={mapping.catalog}
              armed={armed}
              onArm={arm}
              activeLoopPaths={activeLoopPaths}
              onCreateMetadataKey={() => setPending({ kind: 'newKey' })}
            />
          </Paper>
        </Group>
      )}
    </Container>
  );
}

function armedHint(armed: ArmedItem, pinStart: number | null): string {
  const pinNote =
    pinStart !== null ? ` — start pinned at block ${pinStart}; click the last block` : '';
  switch (armed.type) {
    case 'value':
      return `Binding "${armed.label}" — click the target cell or paragraph`;
    case 'collection':
      return `Loop over ${armed.label} — click a table row (one row per item), or click the first and last block of a repeating region${pinNote}`;
    case 'component':
      return `Binding section "${armed.name}" — click the first and last block of its region${pinNote}`;
    case 'subjectLoop':
      return `Subject loop — click the first and last block of the per-subject region${pinNote}`;
    case 'gate':
      return `Subject-type gate — click the first and last block of the region${pinNote}`;
    case 'emptyState':
      return `Empty state — click the first and last block of the authored empty-state region${pinNote}`;
    default:
      return '';
  }
}

// ------------------------------------------------------------------- canvas

interface CanvasBlockProps {
  block: RegionBlock;
  regions: RegionTree;
  loops: LocatedLoop[];
  anchors: Map<string, Binding>;
  pinStart: number | null;
  onBlockClick: (pos: number) => void;
  onCellClick: (pos: number, row: number, col: number) => void;
  onRowClick: (pos: number, row: number) => void;
  onAnnotationClick: (binding: Binding) => void;
}

function CanvasBlock({
  block,
  regions,
  loops,
  anchors,
  pinStart,
  onBlockClick,
  onCellClick,
  onRowClick,
  onAnnotationClick,
}: CanvasBlockProps) {
  const blockLoops = loops.filter(
    (loop) => loop.span && block.pos >= loop.span.start && block.pos <= loop.span.end
  );
  const depth = blockLoops.length;
  const startingLoops = blockLoops.filter((loop) => loop.span!.start === block.pos);

  // Bindings anchored on this block (values in paragraphs; sections by range base).
  const blockBindings: Array<{ binding: Binding; row?: number; col?: number }> = [];
  for (const [name, binding] of anchors) {
    const span = regions.bookmarks[name];
    if (!span || span.start_pos !== block.pos) {
      continue;
    }
    blockBindings.push({ binding, row: span.row, col: span.col });
  }

  const paragraphChips = blockBindings.filter((entry) => entry.row === undefined);
  const rowLoopRows = new Set(
    loops.filter((l) => l.rowAt?.pos === block.pos).map((l) => l.rowAt!.row)
  );

  const isPinStart = pinStart === block.pos;
  const heading = block.kind === 'heading';

  return (
    <div style={{ paddingLeft: depth * 14, position: 'relative' }}>
      {startingLoops.map((loop) => (
        <Group key={loop.binding.id} gap={6} my={2}>
          <Badge
            size="xs"
            color={loop.binding.over === 'subjects' ? 'blue' : 'grape'}
            variant="light"
            leftSection={<IconRepeat size={10} />}
            style={{ cursor: 'pointer' }}
            onClick={() => onAnnotationClick(loop.binding)}
          >
            {loop.binding.over === 'subjects' ? 'SUBJECT LOOP' : loop.binding.over}
          </Badge>
          {loop.binding.empty_region && (
            <Badge size="xs" variant="outline" color="orange">
              empty state set
            </Badge>
          )}
          {loop.binding.applicable_subject_types?.length ? (
            <Badge size="xs" variant="outline">
              {loop.binding.applicable_subject_types.join('/')}
            </Badge>
          ) : null}
        </Group>
      ))}

      <div
        role={block.bindable ? 'button' : undefined}
        tabIndex={block.bindable ? 0 : undefined}
        onClick={() => block.bindable && onBlockClick(block.pos)}
        onKeyDown={(event) => {
          if (block.bindable && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onBlockClick(block.pos);
          }
        }}
        style={{
          cursor: block.bindable ? 'pointer' : 'default',
          opacity: block.bindable ? 1 : 0.45,
          borderLeft: depth > 0 ? '2px solid var(--mantine-color-grape-3)' : undefined,
          paddingLeft: depth > 0 ? 8 : 0,
          background: isPinStart ? 'var(--mantine-color-blue-light)' : undefined,
          borderRadius: 4,
        }}
      >
        {block.kind !== 'table' && (
          <Group gap={6} wrap="nowrap" align="baseline">
            <Text
              size={heading ? 'md' : 'sm'}
              fw={heading ? 700 : 400}
              style={{ whiteSpace: 'pre-wrap', minWidth: 0 }}
              c={block.preview ? undefined : 'dimmed'}
            >
              {block.preview || '¶'}
            </Text>
            {paragraphChips.map(({ binding }) => (
              <BindingChip key={binding.id} binding={binding} onClick={onAnnotationClick} />
            ))}
            {isPinStart && (
              <Badge size="xs" color="blue">
                starts here
              </Badge>
            )}
          </Group>
        )}

        {block.kind === 'table' && isPinStart && (
          <Badge size="xs" color="blue">
            starts here
          </Badge>
        )}

        {block.table && (
          <Table withColumnBorders withTableBorder my={4}>
            <Table.Tbody>
              {block.table.rows.map((row) => (
                <Table.Tr key={row.index}>
                  <Table.Td
                    onClick={(event) => {
                      event.stopPropagation();
                      onRowClick(block.pos, row.index);
                    }}
                    style={{
                      cursor: 'pointer',
                      width: 34,
                      background: 'var(--mantine-color-gray-light)',
                    }}
                  >
                    <Group gap={2} wrap="nowrap">
                      <Text size="xs" c="dimmed">
                        {row.index}
                      </Text>
                      {rowLoopRows.has(row.index) && (
                        <Tooltip label="Loop row: repeats once per item">
                          <IconRepeat size={11} color="var(--mantine-color-grape-6)" />
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                  {row.cells.map((cell) => {
                    const cellBindings = blockBindings.filter(
                      (entry) => entry.row === cell.row && entry.col === cell.col
                    );
                    return (
                      <Table.Td
                        key={cell.col}
                        colSpan={cell.col_span}
                        rowSpan={cell.row_span}
                        onClick={(event) => {
                          event.stopPropagation();
                          onCellClick(block.pos, cell.row, cell.col);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <Group gap={4} wrap="nowrap">
                          <Text size="xs" style={{ minWidth: 0 }}>
                            {cell.preview}
                          </Text>
                          {cellBindings.map(({ binding }) => (
                            <BindingChip
                              key={binding.id}
                              binding={binding}
                              onClick={onAnnotationClick}
                            />
                          ))}
                        </Group>
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </div>
    </div>
  );
}

function BindingChip({
  binding,
  onClick,
}: {
  binding: Binding;
  onClick: (binding: Binding) => void;
}) {
  const label =
    binding.kind === 'value'
      ? `⇐ ${binding.source.split('.').at(-1)}${binding.replace_text ? ' (substring)' : ''}`
      : binding.kind === 'section'
        ? binding.component
          ? `§ ${binding.component}`
          : binding.source
            ? `§ ${binding.source}`
            : `⛨ ${binding.applicable_subject_types?.join('/')}`
        : '';
  return (
    <Chip
      size="xs"
      checked={false}
      variant="light"
      onClick={(event) => {
        event.stopPropagation();
        onClick(binding);
      }}
    >
      {label}
    </Chip>
  );
}

// -------------------------------------------------------------- pending card

interface PendingCardProps {
  pending: Pending;
  /** Full text of the value target (paragraph or cell), when known. */
  targetText?: string;
  loops: LocatedLoop[];
  isWorking: boolean;
  replaceText: string;
  setReplaceText: (v: string) => void;
  valueFormat: string | null;
  setValueFormat: (v: string | null) => void;
  /** Names of the config's appearance sets — offered as "Colour by value"
   * options on an enum-typed value binding. */
  appearanceSetNames: string[];
  appearanceSetName: string | null;
  setAppearanceSetName: (v: string | null) => void;
  keepContent: boolean;
  setKeepContent: (v: boolean) => void;
  gateType: string | null;
  setGateType: (v: string | null) => void;
  emptyTargetChain: string | null;
  setEmptyTargetChain: (v: string | null) => void;
  newKeyName: string;
  setNewKeyName: (v: string) => void;
  newKeyValue: string;
  setNewKeyValue: (v: string) => void;
  onCreateKey: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  onDelete: (id: string) => void;
  /** Record fields of the inspected loop's collection (style_by sources). */
  styleByFields: CatalogValue[];
  /** Style names the template defines (style_by targets). */
  styleOptions: string[];
  onSetStyleBy: (id: string, rule: StyleByRule | null) => void;
}

function PendingCard({
  pending,
  targetText,
  loops,
  isWorking,
  replaceText,
  setReplaceText,
  valueFormat,
  setValueFormat,
  appearanceSetNames,
  appearanceSetName,
  setAppearanceSetName,
  keepContent,
  setKeepContent,
  gateType,
  setGateType,
  emptyTargetChain,
  setEmptyTargetChain,
  newKeyName,
  setNewKeyName,
  newKeyValue,
  setNewKeyValue,
  onCreateKey,
  onConfirm,
  onCancel,
  onDelete,
  styleByFields,
  styleOptions,
  onSetStyleBy,
}: PendingCardProps) {
  return (
    <Paper
      withBorder
      shadow="md"
      p="sm"
      style={{
        position: 'absolute',
        bottom: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10,
        minWidth: 420,
        maxWidth: '90%',
      }}
    >
      <Group justify="space-between" mb={6}>
        <Title order={6}>{pendingTitle(pending)}</Title>
        <Button size="compact-xs" variant="subtle" onClick={onCancel}>
          <IconX size={14} />
        </Button>
      </Group>

      {pending.kind === 'value' && pending.valueType === 'image' && (
        <Text size="xs" c="dimmed">
          Placed as a picture that replaces the whole paragraph, sized to fit the page (or cell)
          width. The paragraph&apos;s alignment and spacing apply to it.
        </Text>
      )}

      {pending.kind === 'value' && pending.valueType !== 'image' && (
        <Stack gap={6}>
          <TextInput
            size="xs"
            label="Only replace this text (optional)"
            description="Leave empty to replace the whole paragraph; state the exact placeholder to swap just that substring"
            value={replaceText}
            onChange={(e) => setReplaceText(e.currentTarget.value)}
            error={replaceTextError(replaceText, targetText)}
          />
          <Select
            size="xs"
            label="Format"
            description="Empty = plain text"
            data={[
              { value: 'date', label: 'Date (e.g. 17 July 2026)' },
              { value: 'hyperlink', label: 'Hyperlink' },
            ]}
            clearable
            value={valueFormat}
            onChange={setValueFormat}
          />
          {pending.valueType === 'enum' && appearanceSetNames.length > 0 && (
            <>
              <Select
                size="xs"
                label="Colour by value"
                description="Style this cell to match its value in the legend you drew (set up under “Colour coding”)"
                placeholder="Don't colour this cell"
                data={appearanceSetNames}
                clearable
                value={appearanceSetName}
                onChange={setAppearanceSetName}
              />
              {appearanceSetName && (
                <SegmentedControl
                  size="xs"
                  fullWidth
                  data={[
                    { value: 'value', label: 'Show the value as text' },
                    { value: 'glyph', label: "Keep the cell's symbol" },
                  ]}
                  value={keepContent ? 'glyph' : 'value'}
                  onChange={(v) => setKeepContent(v === 'glyph')}
                />
              )}
            </>
          )}
        </Stack>
      )}

      {pending.kind === 'section' && pending.isData && (
        <Alert color="orange" py={6} mb={6}>
          <Text size="xs">
            {pending.name} has structured output, not prose — this marks its region; bind its values
            and loops inside it.
          </Text>
        </Alert>
      )}

      {pending.kind === 'gate' && (
        <Select
          size="xs"
          label="Keep this region only for"
          placeholder="Choose subject type"
          data={SUBJECT_TYPE_OPTIONS}
          value={gateType}
          onChange={setGateType}
        />
      )}

      {pending.kind === 'emptyState' && (
        <Select
          size="xs"
          label="Empty state for which loop?"
          data={loops.map((loop) => ({
            value: loop.idChain.join('>'),
            label: loop.binding.over,
          }))}
          value={emptyTargetChain}
          onChange={setEmptyTargetChain}
        />
      )}

      {pending.kind === 'newKey' && (
        <Group gap="xs" align="flex-end">
          <TextInput
            size="xs"
            label="Key"
            placeholder="matter_ref"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.currentTarget.value)}
            error={
              newKeyName && !METADATA_KEY_RE.test(newKeyName)
                ? 'Letters, digits, _ and - only'
                : undefined
            }
          />
          <TextInput
            size="xs"
            label="Default value"
            placeholder="(optional)"
            value={newKeyValue}
            onChange={(e) => setNewKeyValue(e.currentTarget.value)}
          />
          <Button
            size="xs"
            onClick={onCreateKey}
            loading={isWorking}
            disabled={!newKeyName || !METADATA_KEY_RE.test(newKeyName)}
          >
            Create
          </Button>
        </Group>
      )}

      {pending.kind === 'inspect' && (
        <Stack gap={6}>
          <Group gap={6}>
            <Badge size="xs">{pending.binding.kind}</Badge>
            <Code>
              {pending.binding.kind === 'value' && pending.binding.source}
              {pending.binding.kind === 'section' &&
                (pending.binding.component ??
                  pending.binding.source ??
                  `gate: ${pending.binding.applicable_subject_types?.join('/')}`)}
              {pending.binding.kind === 'repeat' && pending.binding.over}
            </Code>
            {pending.binding.kind === 'value' && pending.binding.replace_text && (
              <Text size="xs" c="dimmed">
                replaces “{pending.binding.replace_text}”
              </Text>
            )}
          </Group>
          {pending.binding.kind === 'repeat' && (
            <StyleByEditor
              key={pending.binding.id}
              binding={pending.binding}
              fields={styleByFields}
              styles={styleOptions}
              onSave={onSetStyleBy}
            />
          )}
          <Button
            size="xs"
            color="red"
            variant="light"
            leftSection={<IconTrash size={12} />}
            onClick={() => onDelete(pending.binding.id)}
          >
            Remove binding
          </Button>
        </Stack>
      )}

      {pending.kind !== 'inspect' && pending.kind !== 'newKey' && (
        <Group mt={8}>
          <Button
            size="xs"
            onClick={onConfirm}
            loading={isWorking}
            disabled={
              pending.kind === 'value' && Boolean(replaceTextError(replaceText, targetText))
            }
          >
            Anchor and bind
          </Button>
          <Button size="xs" variant="subtle" onClick={onCancel}>
            Cancel
          </Button>
        </Group>
      )}
    </Paper>
  );
}

/** Mirror of the backend validator's replace_text rule: the placeholder
 * must occur exactly once in the target's text. Literal match, not regex. */
function replaceTextError(needle: string, targetText?: string): string | null {
  if (!needle.trim() || targetText === undefined) {
    return null;
  }
  const occurrences = targetText.split(needle).length - 1;
  if (occurrences === 0) {
    return 'Not found in the selected text';
  }
  if (occurrences > 1) {
    return `Appears ${occurrences} times — use a longer, unique snippet`;
  }
  return null;
}

/** Conditional table styling for a loop: map a record field's values to
 * template table styles (fill swaps each unit's table style — e.g. colour an
 * adverse-media finding's table by its risk_label). */
function StyleByEditor({
  binding,
  fields,
  styles,
  onSave,
}: {
  binding: RepeatBinding;
  fields: CatalogValue[];
  styles: string[];
  onSave: (id: string, rule: StyleByRule | null) => void;
}) {
  const existing = binding.style_by ?? null;
  const [source, setSource] = useState<string | null>(existing?.source ?? null);
  const [rules, setRules] = useState<Array<[string, string]>>(
    existing ? Object.entries(existing.map) : []
  );
  const [fallback, setFallback] = useState<string | null>(existing?.default ?? null);

  const styleData = styles.map((s) => ({ value: s, label: s }));
  const complete = source && fallback && rules.length > 0 && rules.every(([v, s]) => v.trim() && s);

  const save = () => {
    if (!complete || !source || !fallback) {
      return;
    }
    const map: Record<string, string> = {};
    for (const [value, style] of rules) {
      map[value.trim()] = style;
    }
    onSave(binding.id, { source, map, default: fallback });
    notifications.show({ color: 'green', message: 'Conditional styling saved' });
  };

  return (
    <Stack gap={6} mt={4}>
      <Group justify="space-between">
        <Text size="xs" fw={600}>
          Conditional table styling
        </Text>
        {existing && (
          <Badge size="xs" color="grape" variant="light">
            active
          </Badge>
        )}
      </Group>
      <Select
        size="xs"
        label="Style by which field"
        placeholder="Choose a record field"
        searchable
        data={fields.map((f) => ({ value: f.path, label: `${f.label} (${f.path})` }))}
        value={source}
        onChange={setSource}
      />
      {rules.map(([value, style], i) => (
        <Group key={i} gap={4} wrap="nowrap" align="flex-end">
          <TextInput
            size="xs"
            label={i === 0 ? 'When value is' : undefined}
            placeholder="e.g. HIGH"
            value={value}
            onChange={(e) =>
              setRules((rs) => rs.map((r, j) => (j === i ? [e.currentTarget.value, r[1]] : r)))
            }
            style={{ flex: 1 }}
          />
          <Select
            size="xs"
            label={i === 0 ? 'use style' : undefined}
            placeholder="Template style"
            searchable
            data={styleData}
            value={style || null}
            onChange={(v) => setRules((rs) => rs.map((r, j) => (j === i ? [r[0], v ?? ''] : r)))}
            style={{ flex: 1 }}
          />
          <Button
            size="compact-xs"
            variant="subtle"
            color="red"
            onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))}
          >
            <IconX size={12} />
          </Button>
        </Group>
      ))}
      <Button size="compact-xs" variant="light" onClick={() => setRules((rs) => [...rs, ['', '']])}>
        Add value → style
      </Button>
      <Select
        size="xs"
        label="Default style (any other value)"
        placeholder="Template style"
        searchable
        data={styleData}
        value={fallback}
        onChange={setFallback}
      />
      <Group gap={6}>
        <Button size="xs" onClick={save} disabled={!complete}>
          Save styling
        </Button>
        {existing && (
          <Button
            size="xs"
            variant="subtle"
            color="red"
            onClick={() => {
              setSource(null);
              setRules([]);
              setFallback(null);
              onSave(binding.id, null);
            }}
          >
            Clear
          </Button>
        )}
      </Group>
    </Stack>
  );
}

// ---------------------------------------------------- appearance-set builder

interface AppearanceSetBuilderProps {
  sets: Record<string, AppearanceSet>;
  onChange: (next: Record<string, AppearanceSet>) => void;
  enumFieldOptions: CatalogValue[];
  regions: RegionTree | null;
  onMatchLegend: (setName: string) => void;
  onRepick: (setName: string, value: string) => void;
}

function AppearanceSetBuilder({
  sets,
  onChange,
  enumFieldOptions,
  regions,
  onMatchLegend,
  onRepick,
}: AppearanceSetBuilderProps) {
  const names = Object.keys(sets);

  const addSet = () => {
    let n = names.length + 1;
    let name = `Scheme ${n}`;
    while (sets[name]) {
      name = `Scheme ${++n}`;
    }
    onChange({ ...sets, [name]: { level: 'cell', map: {} } });
  };

  const renameSet = (from: string, to: string) => {
    const next = to.trim();
    if (!next || next === from || sets[next]) {
      return;
    }
    // Preserve insertion order — rebuild the record swapping the one key.
    const rebuilt: Record<string, AppearanceSet> = {};
    for (const [k, v] of Object.entries(sets)) {
      rebuilt[k === from ? next : k] = v;
    }
    onChange(rebuilt);
  };

  const updateSet = (name: string, set: AppearanceSet) => {
    onChange({ ...sets, [name]: set });
  };

  const deleteSet = (name: string) => {
    const next = { ...sets };
    delete next[name];
    onChange(next);
  };

  return (
    <Stack gap="sm">
      {names.length === 0 && (
        <Text size="xs" c="dimmed">
          Nothing set up yet. Add a colour scheme, pick the field it colours, then match each value
          to your template legend.
        </Text>
      )}
      {names.map((name) => (
        <AppearanceSetCard
          key={name}
          name={name}
          set={sets[name]}
          existingNames={names}
          enumFieldOptions={enumFieldOptions}
          regions={regions}
          onRename={(to) => renameSet(name, to)}
          onChange={(set) => updateSet(name, set)}
          onDelete={() => deleteSet(name)}
          onMatchLegend={() => onMatchLegend(name)}
          onRepick={(value) => onRepick(name, value)}
        />
      ))}
      <Button
        size="xs"
        variant="light"
        leftSection={<IconPlus size={14} />}
        onClick={addSet}
        style={{ alignSelf: 'flex-start' }}
      >
        Add colour scheme
      </Button>
    </Stack>
  );
}

/** True when a field's closed vocabulary is exactly the set's current value
 * rows — used to reflect which enum field a loaded set was built from. */
function fieldMatchesVocabulary(field: CatalogValue, mapKeys: string[]): boolean {
  const allowed = field.allowed_values ?? [];
  if (allowed.length !== mapKeys.length) {
    return false;
  }
  const keys = new Set(mapKeys);
  return allowed.every((v) => keys.has(v));
}

function AppearanceSetCard({
  name,
  set,
  existingNames,
  enumFieldOptions,
  regions,
  onRename,
  onChange,
  onDelete,
  onMatchLegend,
  onRepick,
}: {
  name: string;
  set: AppearanceSet;
  existingNames: string[];
  enumFieldOptions: CatalogValue[];
  regions: RegionTree | null;
  onRename: (to: string) => void;
  onChange: (set: AppearanceSet) => void;
  onDelete: () => void;
  onMatchLegend: () => void;
  onRepick: (value: string) => void;
}) {
  const [nameDraft, setNameDraft] = useState(name);
  const values = Object.keys(set.map);
  const seededField = enumFieldOptions.find((f) => fieldMatchesVocabulary(f, values));
  const [vocab, setVocab] = useState<string | null>(seededField?.path ?? null);

  const commitName = () => {
    const next = nameDraft.trim();
    if (!next || next === name) {
      setNameDraft(name);
      return;
    }
    if (existingNames.includes(next)) {
      notifications.show({ color: 'orange', message: `“${next}” already exists` });
      setNameDraft(name);
      return;
    }
    onRename(next);
  };

  const seedVocabulary = (path: string | null) => {
    setVocab(path);
    if (!path) {
      return;
    }
    const field = enumFieldOptions.find((f) => f.path === path);
    if (!field?.allowed_values) {
      return;
    }
    // Preserve any exemplar already matched for a value that survives the swap.
    const map: Record<string, string> = {};
    for (const v of field.allowed_values) {
      map[v] = set.map[v] ?? '';
    }
    onChange({ ...set, map });
  };

  const matchedCount = values.filter((v) => set.map[v]).length;
  const defaultOptions = values
    .filter((v) => set.map[v])
    .map((v) => ({ value: set.map[v], label: v }));

  return (
    <Paper withBorder p="sm">
      <Group justify="space-between" wrap="nowrap" mb={2}>
        <TextInput
          size="xs"
          placeholder="Scheme name"
          value={nameDraft}
          onChange={(e) => setNameDraft(e.currentTarget.value)}
          onBlur={commitName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            }
          }}
          style={{ flex: 1 }}
        />
        <Tooltip
          multiline
          w={240}
          label="What to copy from each example cell: the whole cell (its background, shading and borders) or just the text's colour and font."
        >
          <SegmentedControl
            size="xs"
            data={[
              { value: 'cell', label: 'Whole cell' },
              { value: 'run', label: 'Text only' },
            ]}
            value={set.level}
            onChange={(v) => onChange({ ...set, level: v as 'run' | 'cell' })}
          />
        </Tooltip>
        <Button size="compact-xs" variant="subtle" color="red" onClick={onDelete}>
          <IconTrash size={14} />
        </Button>
      </Group>

      <Select
        size="xs"
        label="Field to colour by"
        placeholder="Choose a field"
        description="Its possible values appear below, ready to match"
        searchable
        data={enumFieldOptions.map((f) => ({ value: f.path, label: `${f.label} (${f.path})` }))}
        value={vocab}
        onChange={seedVocabulary}
      />

      {values.length > 0 && (
        <Stack gap={4} mt={8}>
          <Group justify="space-between">
            <Text size="xs" fw={600}>
              Match each value to an example cell
            </Text>
            <Badge
              size="xs"
              variant="light"
              color={matchedCount === values.length ? 'green' : 'gray'}
            >
              {matchedCount}/{values.length} matched
            </Badge>
          </Group>
          {values.map((value) => {
            const bookmark = set.map[value];
            const preview = bookmark && regions ? bookmarkPreview(bookmark, regions) : null;
            return (
              <Group key={value} gap={6} wrap="nowrap" align="center">
                <Text size="xs" style={{ flex: '0 0 40%' }}>
                  {value}
                </Text>
                <Text size="xs" c={bookmark ? undefined : 'dimmed'} style={{ flex: 1 }} truncate>
                  {bookmark ? (preview ?? bookmark) : 'not matched yet'}
                </Text>
                <Button size="compact-xs" variant="subtle" onClick={() => onRepick(value)}>
                  {bookmark ? 'Re-pick' : 'Pick'}
                </Button>
              </Group>
            );
          })}
          <Group gap={6} mt={4}>
            <Button size="compact-xs" variant="light" onClick={onMatchLegend}>
              Auto-match to legend
            </Button>
            <Text size="xs" c="dimmed">
              or use the row buttons to pick each cell by hand
            </Text>
          </Group>
          <Select
            size="xs"
            mt={4}
            label="Fallback for any other value"
            description="Used when a value has no example of its own"
            placeholder="None"
            data={defaultOptions}
            value={set.default ?? null}
            onChange={(v) => onChange({ ...set, default: v })}
            clearable
          />
        </Stack>
      )}
    </Paper>
  );
}

function pendingTitle(pending: Pending): string {
  switch (pending.kind) {
    case 'value':
      return `Bind value — ${pending.label}`;
    case 'rowLoop':
      return `Loop row over ${pending.label}`;
    case 'blockLoop':
      return `Loop blocks ${pending.start}–${pending.end} over ${pending.label}`;
    case 'section':
      return `Bind section — ${pending.name}`;
    case 'subjectLoop':
      return `Subject loop over blocks ${pending.start}–${pending.end}`;
    case 'gate':
      return `Gate blocks ${pending.start}–${pending.end}`;
    case 'emptyState':
      return `Empty state (blocks ${pending.start}–${pending.end})`;
    case 'newKey':
      return 'New report metadata key';
    case 'inspect':
      return 'Binding';
    default:
      return '';
  }
}

function ValidationIssues({
  report,
  onRemove,
}: {
  report: BindingValidationReport;
  onRemove: (id: string) => void;
}) {
  return (
    <Alert color="orange" title={`${report.issues.length} validation issue(s)`} mb="md">
      <Stack gap={4}>
        {report.issues.map((issue, index) => (
          <Group gap={6} key={`${issue.rule}-${index}`} wrap="nowrap" align="flex-start">
            <Badge size="xs">{issue.rule}</Badge>
            {issue.binding_id && <Code>{issue.binding_id}</Code>}
            <Text size="sm" style={{ flex: 1 }}>
              {issue.message}
            </Text>
            {issue.binding_id && (issue.rule === 'path' || issue.rule === 'anchor') && (
              <Tooltip label="Remove this binding — it doesn't resolve here">
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="red"
                  leftSection={<IconTrash size={12} />}
                  onClick={() => onRemove(issue.binding_id!)}
                >
                  Remove
                </Button>
              </Tooltip>
            )}
          </Group>
        ))}
      </Stack>
    </Alert>
  );
}
